import { Injectable } from '@angular/core';

import { BetslipConfig, TeaserBetConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, filter, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { isBetBuilderPickId } from '../../../core/utils';
import BetslipComboBetActions from '../../combo-bet/actions';
import BetslipPickActions from '../../picks/actions';
import { betslipPicksListSelector } from '../../picks/selectors';
import BetslipSystemBetActions from '../../system-bet/actions';
import { BetslipTypesActions } from '../actions';
import { betslipTypesBaseSelector } from '../base/selectors';
import { betslipTypeStateSelector } from '../selectors';

@Injectable()
export default class PickTypeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private teaserBetConfig: TeaserBetConfig,
        private betslipConfig: BetslipConfig,
    ) {}

    addPickInitBetslipEffect$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            concatLatestFrom(() => [this.store.select(betslipTypesBaseSelector), this.store.select(selectIsLinearBetslip)]),
            filter(([, betslipTypesBase]) => betslipTypesBase.currentSelectedType !== BetslipType.EditBet),
            concatMap(function* ([action, betsliptypes, isLinear]): Iterable<Action> {
                const currentTypes = effect.getCurrentTypes(isLinear);

                if (effect.shouldResetTabs(betsliptypes.activeTypes, currentTypes)) {
                    yield BetslipTypesActions.setActiveTypes({ betslipTypes: currentTypes });
                    const betBuilderPick = effect.betslipConfig.isSportcastAsComboEnabled
                        ? isBetBuilderPickId(action.pick.id)
                        : BaseBetslipGroupPick.isPick(action.pick);
                    if (betBuilderPick) {
                        yield BetslipTypesActions.setSelectedType({
                            betslipType: BetslipType.Combo,
                            selectedByUser: false,
                            systemInfo: null,
                        });
                    } else {
                        yield BetslipTypesActions.setSelectedType({
                            betslipType: BetslipType.Single,
                            selectedByUser: false,
                            systemInfo: null,
                        });
                    }
                }
            }),
        );
    });

    replacePickInitBetslipEffect$ = createEffect(() => {
        const self = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            concatLatestFrom(() => [this.store.select(betslipTypesBaseSelector), this.store.select(selectIsLinearBetslip)]),
            filter(([, betslipTypesBase]) => betslipTypesBase.currentSelectedType !== BetslipType.EditBet),
            concatMap(function* ([_, betsliptypes, isLinear]): Iterable<Action> {
                const currentTypes = self.getCurrentTypes(isLinear);

                if (self.shouldResetTabs(betsliptypes.activeTypes, currentTypes)) {
                    yield BetslipTypesActions.setActiveTypes({ betslipTypes: currentTypes });
                    yield BetslipTypesActions.setSelectedType({
                        betslipType: BetslipType.Single,
                        selectedByUser: false,
                        systemInfo: null,
                    });
                }
            }),
        );
    });

    private getCurrentTypes(isLinear: boolean): BetslipType[] {
        const types = [BetslipType.Single, BetslipType.Combo];

        if (isLinear) {
            types.push(BetslipType.BetBuilder);
        }

        if (this.teaserBetConfig.isEnabled) {
            types.push(BetslipType.Teaser);
        }

        if (this.betslipConfig.isSystemBetEnabled) {
            types.push(BetslipType.System);
        }

        return types;
    }

    private shouldResetTabs(existingTypes: BetslipType[], currentTypes: BetslipType[]): boolean {
        return !(existingTypes.length === currentTypes.length && currentTypes.every((x) => existingTypes.includes(x)));
    }

    removePickBetslipEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([_, state]) => !state.length),
            concatMap(() => {
                // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
                return [
                    BetslipTypesActions.setSelectedType({ betslipType: null, selectedByUser: false, systemInfo: null }),
                    BetslipTypesActions.setActiveTypes({ betslipTypes: [] }),
                ];
            }),
        );
    });

    setBetslipTypeEachWay$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.setEachWay),
            concatLatestFrom(() => this.store.select(betslipTypeStateSelector)),
            filter(([_, typeState]) => [BetslipType.Combo, BetslipType.System].includes(typeState.base.currentSelectedType!)),
            map(([action, typeState]) =>
                typeState.base.currentSelectedType === BetslipType.Combo
                    ? BetslipComboBetActions.setComboEachWay({ isEachWay: action.isEachWay })
                    : BetslipSystemBetActions.setSystemEachWay({ isEachWay: action.isEachWay }),
            ),
        );
    });
}
